import React, { useState } from 'react';
import { useColorScheme, useTheme } from '@mui/joy/styles';
import Badge from '@mui/joy/Badge';
import Box from '@mui/joy/Box';
import IconButton from '@mui/joy/IconButton';
import Stack from '@mui/joy/Stack';
import Button from '@mui/joy/Button';
import Tooltip from '@mui/joy/Tooltip';
import List from '@mui/joy/List';
import ListSubheader from '@mui/joy/ListSubheader';
import ListDivider from '@mui/joy/ListDivider';
import ListItem from '@mui/joy/ListItem';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import ListItemContent from '@mui/joy/ListItemContent';
import Sheet from '@mui/joy/Sheet';
import LinearProgress from '@mui/joy/LinearProgress';

import CloseIcon from '@mui/icons-material/CloseRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@providers/AuthProvider';
import {
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    useHover,
    useFloating,
    FloatingPortal,
    flip,
    offset,
    size,
    autoUpdate,
} from '@floating-ui/react';
import { flushSync } from 'react-dom';
import { useClearNotifications, useGetAllNotifications, useUpdateNotification } from '@api/notificationService';
import { sortByDate } from '@utils/sorters';
import pluralize from 'pluralize';

import { formatDate } from '@utils/dates';

const mapObjectTypeToUrl = { // todo: abstract to helper
    contact: 'contacts',
    workplan: 'work-plans',
    'work-plan': 'work-plans',
    business: 'businesses',
    property: 'properties',
    event: 'events',
};

const taskDueEvents = {
    task_overdue: 'overdue',
    task_due_soon: 'due soon',
    task_due_today: 'due today',
};

interface NotificationDataObject {
    id: number,
    name: string,
}

interface NotificationData {
    contact?: NotificationDataObject,
    workplan?: NotificationDataObject,
    workplan_task?: NotificationDataObject,
    business?: NotificationDataObject,
    property?: NotificationDataObject,
    event?: NotificationDataObject,
}

interface Notification {
    id: number,
    type: 'contact' | 'workplan' | 'workplan_task' | 'business' | 'property' | 'event',
    data: NotificationData,
    event: 'created' | 'updated' | 'access_granted' | 'task_overdue' | 'task_due_soon' | 'task_due_today';
    created_at: string,
}

function NotificationDetails({ notification }:{notification: Notification}) {
    const theme = useTheme();
    const updateNotification = useUpdateNotification(notification.id);

    if(typeof notification.data[notification.type] === 'undefined') {
        console.log(notification);
        return <em>Invalid notification</em>;
    }

    const affectedObject = notification.data[notification.type];

    let href;
    let text = '';
    let textObjects = [];
    let object = null;
    let secondaryObject = null;

    if(notification.type === 'workplan_task') {
        href = `/work-plans/${notification.data.workplan.id}`;
        object = <Link to={href} style={{display: 'inline'}}>{notification.data.workplan.name}</Link>;
        secondaryObject = <Link to={`${href}?task=${affectedObject.id}`} style={{display: 'inline'}}>{affectedObject.name}</Link>;

        if(notification.event == 'access_granted')
        {
            text = 'You have been added to the task %obj% on %obj%.';

        }
        else if(typeof taskDueEvents[notification.event] !== 'undefined')
        {
            text = `The task %obj% on %obj is ${taskDueEvents[notification.event]}.`;
        }
        else if (affectedObject.name == 'N/A')
        {
            text =`A task on ${affectedObject.name} has been ${notification.event}, but no longer exists.`;
            // text = `The ${objectName} ${affectedObject.name} has been ${notification.event}, but no longer exists.`;
        }
        else
        {
            text =`The task %obj% on %obj% has been ${notification.event}.`;
        }

        textObjects = [secondaryObject, object];
    } else {
        href = `/${mapObjectTypeToUrl[notification.type]}/${affectedObject.id}`;
        object = <Link to={href} style={{display: 'inline'}}>{affectedObject.name}</Link>;

        let objectName = notification.type === 'workplan' ? 'work plan' : notification.type;

        if(notification.event == 'access_granted')
        {
            text = `You have been added to the ${objectName} %obj%.`;
            textObjects = [object];
        }
        else if (affectedObject.name == 'N/A')
        {
            text = `The ${objectName} ${affectedObject.name} has been ${notification.event}, but no longer exists.`;
        }
        else
        {
            text = `The ${objectName} %obj% has been ${notification.event}.`;
            textObjects = [object];
        }
    }

    function renderText() {
        let objCount = 0;
        const splitText = text.split(/(\%obj\%)/g); // this keeps the separator

        return <div>
            {splitText.map((segment, segemntIndex) => {
                if(segment === '%obj%') {
                    objCount++;
                    return <React.Fragment key={segemntIndex}>{textObjects[objCount-1]}</React.Fragment>;
                }

                return <span style={{display: 'inline'}} key={segemntIndex}>{segment}</span>;
            })}
        </div>;
    }

    // component={Link} to={href} sx={theme => ({color: theme.palette.primary[500]})}
    return (
        // <Tooltip placement='left' title={formatDate(notification.created_at)}>
            <ListItem sx={{fontSize: 'sm'}}>
                <ListItemContent>
                {renderText()}
                {/* {formatDate(notification.created_at)} */}
                </ListItemContent>
                <ListItemDecorator>
                    <IconButton
                        size="sm"
                        variant="plain"
                        color="neutral"
                        sx={{
                            minWidth: '1px',
                            minHeight: '1px',
                            paddingInline: 0,
                            // '--IconButton-size': '1rem',
                            '--Icon-color': 'currentColor',
                            ':hover': {
                                color: theme.palette.danger[500],

                            }
                        }}
                        onClick={() => {
                            updateNotification.mutate({id: notification.id, seen: 1});
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </ListItemDecorator>
            </ListItem>
        // </Tooltip>
    );
}

export default function Notifications() {
    const [isOpen, setIsOpen] = useState(false);
    // const {user, signOut} = useAuth();
    const [maxWidth, setMaxWidth] = useState<number|null>(null);
    const [maxHeight, setMaxHeight] = useState<number|null>(null);
    const { isLoading: isNotificationsLoading, data } = useGetAllNotifications({seen: 0});
    const { mutate: clearAllNotifications, isPending: isClearing } = useClearNotifications();

    const notifications = data ? data.data.sort((a, b) => sortByDate(a, b, 'created_at', 'desc')) : [];

    let notificationsByDate = {};
    notifications.map(notification => {
        const date = formatDate(notification.created_at);
        if(!notificationsByDate[date]) {
            notificationsByDate[date] = [];
        }

        notificationsByDate[date].push(notification);
    })

    const {refs, floatingStyles, context} = useFloating({
        placement: 'bottom-end', // right
        strategy: 'fixed',
        open: isOpen,
        onOpenChange: setIsOpen,
        middleware: [
            offset(10),
            // flip(),
            size({
                apply({availableWidth, availableHeight}) {
                    flushSync(() => {
                        setMaxWidth(Math.min(availableWidth, 350));
                        // setMaxHeight(availableHeight - 16); // make it full height
                        // setMaxHeight(availableHeight * .5); // make it half height
                        setMaxHeight(Math.min(availableHeight, (50*6))); // make it fit ~4
                    });
                },
            }),

            // shift(),
        ],
        whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    // const role = useRole(context);

    // Merge all the interactions into prop getters
    const {getReferenceProps, getFloatingProps} = useInteractions([
        click,
        dismiss,
        // role,
    ]);

    return (<>
        <Tooltip title={!isOpen && (notifications.length ? "Notifications" : "No Notifications")} variant="outlined">
            <Badge invisible={notifications.length === 0} ref={refs.setReference} {...getReferenceProps()}>
                <IconButton
                    size="sm"
                    variant="outlined"
                    color="neutral"
                    sx={{ alignSelf: 'center' }}
                    disabled={!notifications.length}
                >
                    <NotificationsRoundedIcon />
                </IconButton>
            </Badge>
        </Tooltip>
        {isOpen && (
            <FloatingPortal>
                <div
                    ref={refs.setFloating}
                    style={{...floatingStyles, maxWidth, maxHeight, zIndex: 9999}}
                    {...getFloatingProps()}
                >
                    <Sheet
                        variant="outlined"
                        sx={theme => ({
                            // display: { xs: 'none', sm: 'initial' },
                            // width: '100%',
                            borderRadius: 'sm',
                            // flexShrink: 1,
                            // overflow: 'auto',
                            // minHeight: 0,
                            // m: 2,
                            backgroundColor: theme.palette.background,
                            p: isNotificationsLoading ? 2 : 0,
                            width: maxWidth,//'100%',// isNotificationsLoading ? 250 : undefined,
                            maxHeight: maxHeight,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                        })}
                    >
                        {isNotificationsLoading && (
                            <LinearProgress/>
                        )}
                        {!isNotificationsLoading && notifications.length === 0 && (<>
                            <Box sx={{p: 1.5, textAlign: 'center'}}>
                                You are all caught up!
                            </Box>
                        </>)}
                        {!isNotificationsLoading && notifications.length > 0 && (<>
                            <Box
                                sx={theme => ({
                                    backgroundColor: theme.palette.background.level1,
                                    fontSize: 'sm',
                                    // px: 1.5,
                                    pl: 1.5,
                                    pr: 0.5,
                                    py: 0.5,// 1,
                                })}
                            >
                                <Stack
                                      direction="row"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      spacing={2}
                                >
                                    <Box sx={{py: 0.5}}>
                                        {/*
                                        {data.total > data.per_page && (<>
                                            Showing {data.per_page} of {data.total} {pluralize('notification', data.total)}.
                                        </>)}
                                        {data.total <= data.per_page && (<>
                                            You have {data.total} {pluralize('notification', data.total)}.
                                        </>)}
                                        */}
                                        {data.total} {pluralize('notification', data.total)}
                                    </Box>
                                    <Button
                                        size="xs"
                                        variant="plain"
                                        color="neutral"
                                        endDecorator={<CloseIcon/>}
                                        // loadingPosition='end'
                                        loading={isClearing}
                                        onClick={clearAllNotifications}
                                        sx={theme => ({
                                            '--Icon-fontSize': '1.1rem',
                                            '--Button-gap': '0.2rem',
                                            '--Icon-color': 'currentColor',
                                            '--CircularProgress-size': '1em',
                                            '--_progress-thickness': '1px',
                                            '--_track-thickness': '1px',
                                            ':hover': {
                                                color: theme.palette.danger[500],

                                            },
                                            '.MuiCircularProgress-root': {
                                                '--_progress-thickness': '2px',
                                                '--_track-thickness': '2px',
                                            }
                                        })}
                                    >
                                        Clear All
                                    </Button>
                                </Stack>
                            </Box>
                            <List
                                variant="plain"
                                sx={theme => ({
                                    width: maxWidth,
                                    // maxHeight: maxHeight,
                                    height: '100%',
                                    overflowY: 'auto',
                                    borderRadius: 'sm',
                                    backgroundColor: theme.palette.background,
                                    '--ListItemDecorator-size': '1.5rem',
                                    py: 0,
                                })}
                            >
                                {Object.entries(notificationsByDate).map((notificationDate, dateIndex) => (<React.Fragment key={notificationDate[0]}>
                                    <ListSubheader
                                        sticky
                                        sx={theme => ({
                                            /*
                                            pt: 2,
                                            pb: 0.5,
                                            */
                                            borderColor: theme.palette.divider,
                                            borderStyle: 'solid',
                                            borderWidth: 0,
                                            borderTopWidth: dateIndex > 0 && '1px',
                                            borderBottomWidth: '1px',
                                            mt: dateIndex > 0 && 0.5,
                                            mb: 0.5,
                                        })}
                                    >
                                        {notificationDate[0]}
                                    </ListSubheader>
                                    {notificationDate[1].map((notification, index) => (<React.Fragment key={notification.id}>
                                        <NotificationDetails notification={notification}/>
                                        {
                                        index < (notificationDate[1].length - 1) &&
                                        // dateIndex < (Object.entries(notificationsByDate).length - 1) &&
                                        (
                                        <ListDivider />
                                        )}
                                    </React.Fragment>))}
                                </React.Fragment>
                                ))}
                                {data.total > data.per_page && (<>
                                <ListDivider/>
                                <ListItem sx={{pb: 'calc(var(--ListItem-paddingY) + var(--ListDivider-gap))'}}>
                                    <ListItemContent sx={{fontSize: 'xs', fontStyle: 'italic'}}>
                                        Note: Only {data.per_page} of {data.total} {pluralize('notification', data.total)} are displayed.
                                    </ListItemContent>
                                </ListItem>
                                </>)}
                            </List>
                        </>)}
                    </Sheet>
                </div>
            </FloatingPortal>
        )}
    </>);
}

